import React, { useState, useEffect, useContext } from 'react'
import { cloneDeep } from 'lodash'

import TextFilter from './TextFilter'
import { SpanPickerContext } from '../utils/initialStates'
import { getRunTypeOptions, parseOptions } from '../utils/utils'

/**
 * This function filters an incoming tree based on a string (a drilling operation), and outputs a filtered tree
 *
 * @param originalTree {interventionDataTypes[]} - the tree before the filtering
 * @param filterString {string} - the selected operation
 * @returns {interventionDataTypes[]} - the utput tree filtered
 */

const treeFilterByRunType = (
    originalTree: Array<interventionDataTypes>,
    filterString: string[]
): Array<interventionDataTypes> => {
    let treeDataFiltered: Array<interventionDataTypes> = cloneDeep(originalTree)
    treeDataFiltered.forEach(intervention => {
        intervention.children.forEach(section => {
            section.children = section.children.filter(
                run => run.type !== undefined && filterString.includes(run.type.toLowerCase())
            )
        })
        intervention.children = intervention.children.filter(section => section.children.length !== 0)
    })
    return treeDataFiltered.filter(intervention => intervention.children.length !== 0)
}

/**
 * This component filters the tree based on a select component filled with the operations
 *
 * @param tree {TreeComponentProps} - data related to the tree (the items on the tree, an array of keys to all the items and the default expanded nodes)
 * @param select {TreeSelectingProps} - data related to the selection of the tree (the item selected, the function to change its selection, the timespan of the selected item and the run reference of the selected item)
 * @param check {TreeCheckingProps} - data related to the check items of the tree (data of the mainCheck, items checked, quantity of checkedOperations, and an option of using the individual timespans or the min and max of them)
 * @returns {JSX.Element} - a child that will return a JSX Element
 */

const TreeSelectFilter = ({
    select,
    check,
    tree
}: {
    tree: TreeComponentProps
    select: TreeSelectingProps
    check?: TreeCheckingProps
}) => {
    const { operationsPickerConfigs, setOperationsPickerConfigsState } = useContext(SpanPickerContext)
    const [filteredTree, setFilteredTree] = useState(tree.formattedTree)

    const [selectedRunTypeOption, setSelectedRunTypeOption] = useState<RunTypeOptionProps>(
        getRunTypeOptions(operationsPickerConfigs.defaultRunTypeOption) ?? []
    )

    useEffect(() => {
        setFilteredTree(tree.formattedTree)
    }, [JSON.stringify(tree.formattedTree)])

    useEffect(() => {
        const stringValuesOptions = parseOptions(selectedRunTypeOption)?.map(d => d.value.toLowerCase())
        selectedRunTypeOption.length < 1
            ? setFilteredTree(tree.formattedTree)
            : setFilteredTree(treeFilterByRunType(tree.formattedTree, stringValuesOptions))
    }, [selectedRunTypeOption])

    const onChange = (value, valueObj) => {
        setSelectedRunTypeOption(valueObj)
    }

    return (
        <TextFilter
            tree={{
                filteredTree: filteredTree,
                allKeys: tree.allKeys,
                defaultExpandedKeys: tree.defaultExpandedKeys,
                formattedTree: tree.formattedTree,
                runTypesList: tree.runTypesList
            }}
            selectFilter={{
                setSelectedOperationOption: setSelectedRunTypeOption,
                selectedRunTypeOption: selectedRunTypeOption,
                onChange: onChange
            }}
            select={select}
            check={check}
        />
    )
}

export default TreeSelectFilter
